import { API_BASE_URL, apiRequest, type ApiRequestOptions } from '../http.ts';
import type { APIResponse } from './types/common.ts';
import type {
  AllStrategiesStatus,
  MarketAnalysis,
  NQMMPreviousPositionsData,
  StrategyStatus,
  TradingSignal,
} from './types/strategyStatus.ts';

export type StrategyName = 'ON' | 'ORB' | 'DT' | 'NQMM' | 'MORB';

export interface AddNQMMPreviousPositionParams {
  symbol: string;
  quantity: number;
  avg_cost?: number;
  current_price?: number;
  market_value?: number;
  note?: string;
}

export interface StrategySignalFilters {
  strategy?: string;
  symbol?: string;
  start_date?: string;
  end_date?: string;
  page?: number;
  limit?: number;
}

export interface StrategyConfigUpdate<TConfig extends object = Record<string, unknown>> {
  symbols?: string[];
  position_size?: number;
  is_enabled?: boolean;
  leverage?: number;
  orb_entry_time?: string;
  sleeve_allocation?: number;
  cash_buffer?: number;
  top_n?: number;
  max_position_allocation?: number;
  lookback_months?: number;
  skip_recent_months?: number;
  volatility_lookback_months?: number;
  min_momentum?: number;
  rebalance_time?: string;
  semi_automatic?: boolean;
  require_qqq_above_200_sma?: boolean;
  weekly_rebalance_below_qqq_200_sma?: boolean;
  position_sizing_method?: string;
  config_json?: TConfig;
}

export interface StrategyDbConfig {
  enabled: boolean;
  version: number;
  updated_at: string | null;
  config_schema?: Record<string, unknown>;
  config_json: Record<string, unknown>;
  [key: string]: unknown;
}

export interface StrategyDbConfigResponseData {
  strategy: string;
  config: StrategyDbConfig;
}

export interface StrategyConfigHistoryEntry {
  version: number;
  config_json: Record<string, unknown>;
  created_at: string | null;
  strategy_name: string;
}

export interface StrategyConfigHistoryResponseData {
  strategy: string;
  history: StrategyConfigHistoryEntry[];
}

export interface StrategyConfigMutationResponseData {
  strategy: string;
  config?: Record<string, unknown>;
  hot_reloaded?: boolean;
  message?: string;
}

export interface StrategySignalLog {
  id: number;
  timestamp: string;
  strategy: string;
  symbol: string;
  signal: string;
  price: number | null;
  reason: string | null;
  was_executed: boolean;
  decision_id?: string | null;
  metadata_json: Record<string, unknown> | null;
  user_id: number | null;
}

export interface StrategyDbConfigUpdate<TConfig extends object = Record<string, unknown>> {
  is_enabled?: boolean;
  config_json?: TConfig;
}

/** Strategy status, signal history, and user strategy-configuration endpoints. */
export class StrategiesApiService {
  private readonly baseUrl: string;

  constructor(baseUrl: string = API_BASE_URL) {
    this.baseUrl = baseUrl;
  }

  private request<T>(endpoint: string, options: ApiRequestOptions = {}): Promise<T> {
    return apiRequest<T>(endpoint, options);
  }

  getStrategyStatus(): Promise<StrategyStatus> {
    return this.request<StrategyStatus>(`${this.baseUrl}/strategy/on/status`);
  }

  getONStrategyStatus(): Promise<StrategyStatus> {
    return this.request<StrategyStatus>(`${this.baseUrl}/strategy/on/status`);
  }

  getONPositions(): Promise<StrategyStatus> {
    return this.request<StrategyStatus>(`${this.baseUrl}/strategy/on/positions`);
  }

  getORBStrategyStatus(): Promise<StrategyStatus> {
    return this.request<StrategyStatus>(`${this.baseUrl}/strategy/orb/status`);
  }

  getORBPositions(): Promise<APIResponse<Record<string, number>>> {
    return this.request<APIResponse<Record<string, number>>>(
      `${this.baseUrl}/strategy/orb/positions`,
    );
  }

  getAllStrategiesStatus(): Promise<AllStrategiesStatus> {
    return this.request<AllStrategiesStatus>(`${this.baseUrl}/strategy/overview`);
  }

  getTradingSignal(symbol: string): Promise<TradingSignal> {
    return this.request<TradingSignal>(`${this.baseUrl}/strategy/on/signals/${symbol}`);
  }

  getMarketAnalysis(symbol: string): Promise<APIResponse<MarketAnalysis>> {
    return this.request<APIResponse<MarketAnalysis>>(
      `${this.baseUrl}/strategy/on/analysis/${symbol}`,
    );
  }

  getStrategyStatusByName(strategyName: StrategyName): Promise<StrategyStatus> {
    return this.request<StrategyStatus>(`${this.baseUrl}/strategy/status/${strategyName}`);
  }

  getNQMMStrategyStatus(): Promise<StrategyStatus> {
    return this.request<StrategyStatus>(`${this.baseUrl}/strategy/nqmm/status`);
  }

  getNQMMPositions(): Promise<StrategyStatus> {
    return this.request<StrategyStatus>(`${this.baseUrl}/strategy/nqmm/positions`);
  }

  getNQMMPreviousPositions(): Promise<APIResponse<NQMMPreviousPositionsData>> {
    return this.request<APIResponse<NQMMPreviousPositionsData>>(
      `${this.baseUrl}/strategy/nqmm/previous-positions`,
    );
  }

  addNQMMPreviousPosition(
    position: AddNQMMPreviousPositionParams,
  ): Promise<APIResponse<NQMMPreviousPositionsData>> {
    return this.request<APIResponse<NQMMPreviousPositionsData>>(
      `${this.baseUrl}/strategy/nqmm/previous-positions`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(position),
      },
    );
  }

  deleteNQMMPreviousPosition(symbol: string): Promise<APIResponse<NQMMPreviousPositionsData>> {
    return this.request<APIResponse<NQMMPreviousPositionsData>>(
      `${this.baseUrl}/strategy/nqmm/previous-positions/${encodeURIComponent(symbol)}`,
      { method: 'DELETE' },
    );
  }

  getStrategyDbConfig(
    strategyName: string,
  ): Promise<APIResponse<StrategyDbConfigResponseData>> {
    return this.request<APIResponse<StrategyDbConfigResponseData>>(
      `${this.baseUrl}/strategy/config/${strategyName}`,
    );
  }

  getStrategyDbConfigHistory(
    strategyName: string,
  ): Promise<APIResponse<StrategyConfigHistoryResponseData>> {
    return this.request<APIResponse<StrategyConfigHistoryResponseData>>(
      `${this.baseUrl}/strategy/config/${strategyName}/history`,
    );
  }

  getStrategySignals(
    filters: StrategySignalFilters,
  ): Promise<APIResponse<StrategySignalLog[]>> {
    const query = new URLSearchParams();
    if (filters.strategy) query.append('strategy', filters.strategy);
    if (filters.symbol) query.append('symbol', filters.symbol);
    if (filters.start_date) query.append('start_date', filters.start_date);
    if (filters.end_date) query.append('end_date', filters.end_date);
    if (filters.page) query.append('page', filters.page.toString());
    if (filters.limit) query.append('limit', filters.limit.toString());
    return this.request<APIResponse<StrategySignalLog[]>>(
      `${this.baseUrl}/strategy/signals?${query.toString()}`,
    );
  }

  resetStrategyDbConfig(
    strategyName: string,
  ): Promise<APIResponse<StrategyConfigMutationResponseData>> {
    return this.request<APIResponse<StrategyConfigMutationResponseData>>(
      `${this.baseUrl}/strategy/config/${strategyName}/reset`,
      { method: 'POST' },
    );
  }

  updateStrategyConfig<TConfig extends object = Record<string, unknown>>(
    strategyName: string,
    payload: StrategyConfigUpdate<TConfig>,
  ): Promise<APIResponse<StrategyConfigMutationResponseData>> {
    return this.request<APIResponse<StrategyConfigMutationResponseData>>(
      `${this.baseUrl}/strategy/config/${strategyName}`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      },
    );
  }

  updateStrategyDbConfig<TConfig extends object = Record<string, unknown>>(
    strategyName: string,
    payload: StrategyDbConfigUpdate<TConfig>,
  ): Promise<APIResponse<StrategyConfigMutationResponseData>> {
    return this.request<APIResponse<StrategyConfigMutationResponseData>>(
      `${this.baseUrl}/strategy/config/${strategyName}`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      },
    );
  }
}
